import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { AlertTriangle, Flame, Package, Loader2, History, KeyRound, ShieldCheck, Wallet, CheckCircle, XCircle, Clock } from "lucide-react";
import { StatusPill } from "@/components/caregiver/OrderFeed";
import { ChargeLines } from "@/components/caregiver/PaymentsCard";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/** At-a-glance strip: last order, anything needing attention, wellness streak. */
export function StatusStrip({ orders, seniorName }) {
  const [games, setGames] = useState(null);
  useEffect(() => {
    axios.get(`${API}/family/senior-games`).then((r) => setGames(r.data)).catch(() => setGames({ streak: 0 }));
  }, []);

  const last = orders[0];
  const attention = orders.filter((o) => o.needs_attention);

  return (
    <div className="space-y-3" data-testid="caregiver-status-strip">
      {attention.length > 0 && (
        <div className="rounded-2xl bg-rose-50 border border-rose-200 p-4 flex items-start gap-3" data-testid="status-attention">
          <AlertTriangle className="w-5 h-5 text-rose-600 mt-0.5 shrink-0" />
          <div>
            <p className="font-semibold text-rose-700">
              {attention.length} order{attention.length > 1 ? "s" : ""} need{attention.length > 1 ? "" : "s"} attention
            </p>
            <p className="text-sm text-rose-600/80">{attention[0].title} — {attention[0].summary || "tap it below to see what happened"}</p>
          </div>
        </div>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div className="bg-white rounded-2xl border border-gray-200 p-4" data-testid="status-last-order">
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1.5">Last order</p>
          {last ? (
            <>
              <p className="font-semibold text-[#1A172E] truncate">{last.icon} {last.title}</p>
              <div className="mt-1.5"><StatusPill label={last.status_label} tone={last.status_tone} /></div>
            </>
          ) : (
            <p className="text-base text-gray-400 flex items-center gap-2"><Package className="w-4 h-4" /> Nothing yet</p>
          )}
        </div>
        <div className="bg-white rounded-2xl border border-gray-200 p-4" data-testid="status-wellness">
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1.5">Daily Games</p>
          {!games ? <Loader2 className="w-4 h-4 animate-spin text-[#2D2766]" /> : games.last_played_date ? (
            <p className="font-semibold text-[#1A172E] flex items-center gap-1.5">
              <Flame className="w-4 h-4 text-amber-500" /> {games.streak} day{games.streak !== 1 ? "s" : ""} streak
            </p>
          ) : (
            <p className="text-base text-gray-400">{seniorName} hasn't played yet</p>
          )}
        </div>
      </div>
    </div>
  );
}

/** Caregiver activity audit — primary caregiver only. */
export function ActionLogCard() {
  const [actions, setActions] = useState(null);
  useEffect(() => {
    axios.get(`${API}/family/action-log`).then((r) => setActions(r.data.actions || [])).catch(() => setActions([]));
  }, []);
  if (!actions?.length) return null;
  const label = { placed_order: "placed an order", edited_profile: "edited the profile", added_caregiver: "added a caregiver", removed_caregiver: "removed a caregiver" };
  return (
    <div className="bg-white rounded-2xl border border-gray-200 p-5" data-testid="caregiver-action-log">
      <p className="font-semibold text-[#1A172E] flex items-center gap-2 mb-3"><History className="w-5 h-5 text-[#2D2766]" /> Caregiver activity</p>
      <ul className="space-y-2.5">
        {actions.slice(0, 10).map((a) => (
          <li key={a.id} className="text-sm text-[#333]">
            <span className="font-semibold">{a.caregiver_name || a.caregiver_phone}</span>{" "}
            {label[a.action] || a.action.replace(/_/g, " ")}
            {a.detail && <span className="text-gray-400"> — {a.detail}</span>}
            <span className="block text-xs text-gray-400">{new Date(a.created_at).toLocaleString("en-IN")}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/** Every payment permission the senior tapped — wallet access and each individual bill.
 *  Only the primary caregiver may read them, so a 403 simply hides the card. */
export function ConsentLogCard() {
  const [consents, setConsents] = useState(null);
  useEffect(() => {
    axios.get(`${API}/family/consents`).then((r) => setConsents(r.data.consents || [])).catch(() => setConsents([]));
  }, []);
  if (!consents?.length) return null;
  const label = { wallet: "Wallet access", bill_payment: "Bill approved",
                  wallet_declined: "Wallet access refused", bill_declined: "Bill refused",
                  wallet_revoked: "Wallet access stopped" };
  return (
    <div className="bg-white rounded-2xl border border-gray-200 p-5" data-testid="caregiver-consent-log">
      <p className="font-semibold text-[#1A172E] flex items-center gap-2 mb-3">
        <ShieldCheck className="w-5 h-5 text-[#2D2766]" /> Payment permissions
      </p>
      <ul className="space-y-2.5">
        {consents.slice(0, 10).map((c) => (
          <li key={c.id} className="text-sm text-[#333]" data-testid={`consent-row-${c.id}`}>
            <span className="font-semibold">{label[c.kind] || c.kind.replace(/_/g, " ")}</span>
            <span className="text-gray-500"> — {c.detail}</span>
            <span className="block text-xs text-gray-400">
              {new Date(c.created_at).toLocaleString("en-IN")} · {c.channel === "whatsapp" ? "WhatsApp" : "Web"}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/** One-time PIN → the caregiver sets their own on first login. */export function SetPinBanner({ onDone }) {
  const [pin, setPin] = useState("");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (!/^\d{4}$/.test(pin)) { toast.error("Enter a 4-digit PIN."); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/family/change-pin`, { new_pin: pin });
      toast.success("PIN updated — use it next time you log in.");
      onDone();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not update the PIN.");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="rounded-2xl bg-amber-50 border border-amber-200 p-4" data-testid="set-pin-banner">
      <p className="font-semibold text-amber-800 flex items-center gap-2"><KeyRound className="w-4 h-4" /> Set your own PIN</p>
      <p className="text-sm text-amber-700/90 mt-0.5">You logged in with a one-time PIN. Choose your own 4-digit PIN now.</p>
      <div className="flex gap-2 mt-3">
        <input inputMode="numeric" maxLength={4} value={pin} placeholder="New 4-digit PIN"
          onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 4))}
          className="flex-1 min-h-[48px] px-3.5 border-2 border-amber-300 rounded-xl bg-white focus:outline-none focus:border-[#2D2766]"
          data-testid="set-pin-input" />
        <button onClick={save} disabled={busy}
          className="px-5 min-h-[48px] bg-[#2D2766] text-white rounded-xl font-semibold text-sm disabled:opacity-60"
          data-testid="set-pin-save">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save"}</button>
      </div>
    </div>
  );
}

/** Spending cap + approval threshold — primary caregiver only. */
export function BillSettingsCard() {
  const [settings, setSettings] = useState(null);
  const [editing, setEditing] = useState(false);
  const [limit, setLimit] = useState("");
  const [threshold, setThreshold] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    axios.get(`${API}/family/bill-settings`).then((r) => {
      setSettings(r.data);
      setLimit(String(r.data.monthly_limit || ""));
      setThreshold(String(r.data.approval_threshold || ""));
    }).catch(() => setSettings(null));
  }, []);

  const save = async () => {
    const ml = parseFloat(limit);
    const at = parseFloat(threshold);
    if (isNaN(ml) || ml < 0) { toast.error("Enter a valid monthly limit."); return; }
    if (isNaN(at) || at < 0) { toast.error("Enter a valid approval threshold."); return; }
    setBusy(true);
    try {
      const r = await axios.patch(`${API}/family/bill-settings`, { monthly_limit: ml, approval_threshold: at });
      setSettings(r.data);
      setEditing(false);
      toast.success("Bill settings updated.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not save settings.");
    } finally {
      setBusy(false);
    }
  };

  if (!settings) return null;

  const fmt = (v) => `₹${Number(v || 0).toLocaleString("en-IN")}`;

  return (
    <div className="bg-white rounded-2xl border border-gray-200 p-5" data-testid="bill-settings-card">
      <p className="font-semibold text-[#1A172E] flex items-center gap-2 mb-3">
        <Wallet className="w-5 h-5 text-[#2D2766]" /> Bill-payment limits
      </p>
      {!editing ? (
        <>
          <div className="grid grid-cols-2 gap-3 mb-3">
            <div className="rounded-xl bg-gray-50 border border-gray-100 p-3" data-testid="bill-settings-monthly-limit">
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Monthly cap</p>
              <p className="text-lg font-bold text-[#1A172E] mt-0.5">{fmt(settings.monthly_limit)}</p>
              <p className="text-xs text-gray-400">per calendar month</p>
            </div>
            <div className="rounded-xl bg-gray-50 border border-gray-100 p-3" data-testid="bill-settings-approval-threshold">
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Approval needed above</p>
              <p className="text-lg font-bold text-[#1A172E] mt-0.5">{fmt(settings.approval_threshold)}</p>
              <p className="text-xs text-gray-400">per bill</p>
            </div>
          </div>
          <button onClick={() => setEditing(true)} data-testid="bill-settings-edit-btn"
            className="w-full min-h-[44px] border-2 border-[#2D2766] text-[#2D2766] rounded-xl font-semibold text-sm hover:bg-[#2D2766]/5 transition-colors">
            Change limits
          </button>
        </>
      ) : (
        <>
          <div className="space-y-3 mb-4">
            <div>
              <label className="text-xs font-semibold text-gray-500 uppercase">Monthly cap (₹)</label>
              <input type="number" inputMode="numeric" min="0" value={limit}
                onChange={(e) => setLimit(e.target.value)} data-testid="bill-settings-limit-input"
                className="w-full min-h-[48px] px-3.5 border-2 border-gray-200 rounded-xl bg-white focus:outline-none focus:border-[#2D2766] mt-1 text-lg font-semibold" />
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-500 uppercase">Approval needed above (₹)</label>
              <input type="number" inputMode="numeric" min="0" value={threshold}
                onChange={(e) => setThreshold(e.target.value)} data-testid="bill-settings-threshold-input"
                className="w-full min-h-[48px] px-3.5 border-2 border-gray-200 rounded-xl bg-white focus:outline-none focus:border-[#2D2766] mt-1 text-lg font-semibold" />
            </div>
          </div>
          <div className="flex gap-2">
            <button onClick={() => setEditing(false)} data-testid="bill-settings-cancel-btn"
              className="flex-1 min-h-[44px] border-2 border-gray-200 text-gray-600 rounded-xl font-semibold text-sm">Cancel</button>
            <button onClick={save} disabled={busy} data-testid="bill-settings-save-btn"
              className="flex-1 min-h-[44px] bg-[#2D2766] text-white rounded-xl font-semibold text-sm disabled:opacity-60">
              {busy ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : "Save"}
            </button>
          </div>
        </>
      )}
    </div>
  );
}

/** Pending bill approval requests — any family member can action. */
export function PendingApprovalsCard() {
  const [approvals, setApprovals] = useState(null);
  const [busy, setBusy] = useState({});

  const load = () => {
    axios.get(`${API}/family/bill-approvals`).then((r) => setApprovals(r.data.approvals || [])).catch(() => setApprovals([]));
  };
  useEffect(() => { load(); const iv = setInterval(load, 15000); return () => clearInterval(iv); }, []);

  const act = async (id, action) => {
    setBusy((p) => ({ ...p, [id]: action }));
    try {
      await axios.post(`${API}/family/bill-approvals/${id}/${action}`);
      toast.success(action === "approve" ? "Bill approved — payment is proceeding." : "Bill declined.");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || `Could not ${action} the bill.`);
    } finally {
      setBusy((p) => ({ ...p, [id]: null }));
    }
  };

  if (!approvals?.length) return null;

  const pending = approvals.filter((a) => a.status === "pending");
  const resolved = approvals.filter((a) => a.status !== "pending");
  const statusIcon = { approved: <CheckCircle className="w-4 h-4 text-emerald-500" />, declined: <XCircle className="w-4 h-4 text-rose-500" /> };
  const statusLabel = { approved: "Approved", declined: "Declined" };

  return (
    <div className="bg-white rounded-2xl border border-gray-200 p-5" data-testid="pending-approvals-card">
      <p className="font-semibold text-[#1A172E] flex items-center gap-2 mb-3">
        <Clock className="w-5 h-5 text-[#2D2766]" /> Bill approvals
      </p>

      {pending.length > 0 && (
        <div className="space-y-3 mb-4">
          {pending.map((a) => (
            <div key={a.id} className="rounded-xl bg-amber-50 border border-amber-200 p-4" data-testid={`approval-pending-${a.id}`}>
              <p className="font-semibold text-amber-800">₹{Number(a.amount).toLocaleString("en-IN")} to {a.biller_alias || a.biller_name}</p>
              <p className="text-sm text-amber-700/80 mt-0.5">{a.reason}</p>
              <ChargeLines charges={a.charges} baseLabel={a.kind === "swiggy" ? "Food (paid to Swiggy)" : "Bill amount"} testid={`approval-charges-${a.id}`} />
              <p className="text-xs text-amber-600/60 mt-1">{new Date(a.created_at).toLocaleString("en-IN")}</p>
              <div className="flex gap-2 mt-3">
                <button onClick={() => act(a.id, "approve")} disabled={!!busy[a.id]} data-testid={`approve-btn-${a.id}`}
                  className="flex-1 min-h-[44px] bg-emerald-600 text-white rounded-xl font-semibold text-sm disabled:opacity-60 flex items-center justify-center gap-1.5">
                  {busy[a.id] === "approve" ? <Loader2 className="w-4 h-4 animate-spin" /> : <><CheckCircle className="w-4 h-4" /> Approve</>}
                </button>
                <button onClick={() => act(a.id, "decline")} disabled={!!busy[a.id]} data-testid={`decline-btn-${a.id}`}
                  className="flex-1 min-h-[44px] bg-rose-600 text-white rounded-xl font-semibold text-sm disabled:opacity-60 flex items-center justify-center gap-1.5">
                  {busy[a.id] === "decline" ? <Loader2 className="w-4 h-4 animate-spin" /> : <><XCircle className="w-4 h-4" /> Decline</>}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {resolved.length > 0 && (
        <div className="space-y-2">
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Recent decisions</p>
          {resolved.slice(0, 5).map((a) => (
            <div key={a.id} className="flex items-center gap-2 text-sm" data-testid={`approval-resolved-${a.id}`}>
              {statusIcon[a.status] || <Clock className="w-4 h-4 text-gray-400" />}
              <span className="font-semibold text-[#333]">₹{Number(a.amount).toLocaleString("en-IN")} to {a.biller_alias || a.biller_name}</span>
              <span className="text-gray-400 text-xs ml-auto">{statusLabel[a.status] || a.status} · {a.resolved_by || ""}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
